import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '../../../context/LanguageContext';
import { 
    FaSave, 
    FaStore, 
    FaPrint, 
    FaMoneyBillAlt, 
    FaLanguage, 
    FaReceipt, 
    FaUserCog,
    FaTruck 
} from 'react-icons/fa';
import axios from '../../../utils/axios';
import toast from 'react-hot-toast';
import Layout from '../../../components/admin/Layout';

// Import Tab Components
import RestaurantTab from '../../../components/admin/settings/tabs/RestaurantTab';
import PrintingTab from '../../../components/admin/settings/tabs/PrintingTab';
import TaxTab from '../../../components/admin/settings/tabs/TaxTab';
import InterfaceTab from '../../../components/admin/settings/tabs/InterfaceTab';
import ReceiptsTab from '../../../components/admin/settings/tabs/ReceiptsTab';
import DeliveryTab from '../../../components/admin/settings/tabs/DeliveryTab';
import TableServiceTab from '../../../components/admin/settings/tabs/TableServiceTab';
import UsersTab from '../../../components/admin/settings/tabs/UsersTab';

const Settings = () => {
    const { t, isRTL, setLanguage } = useTranslation();
    const [activeTab, setActiveTab] = useState('restaurant');
    const [isLoading, setIsLoading] = useState(false);
    const [settings, setSettings] = useState({
        restaurant: {
            name: '',
            name_ar: '',
            phone: '',
            tax_number: '',
            address: '',
            address_ar: '',
            commercial_registration: ''
        },
        printing: {
            printer_type: 'thermal',
            paper_size: '80mm',
            font_size: '12',
            show_logo: false,
            auto_print: false,
        },
        tax: {
            percentage: 0,
            included_in_price: false,
            show_tax_number: false,
        },
        interface: {
            default_language: 'en',
            items_per_page: '10',
            currency_symbol: '',
        },
        receipts: {
            show_customer_info: false,
            show_order_number: false,
            footer_text: '',
            footer_text_ar: '',
            qr_code: false,
        },
        delivery: {
            enabled: false,
            base_fee: 0,
            min_order_amount: 0,
            free_delivery_threshold: 0
        },
        table_service: {
            enabled: false,
            service_charge: 15,
            charge_type: 'percentage',
            min_order_amount: 0,
            max_service_charge: 0
        },
        users: {
            require_password_change: false,
            session_timeout: 60,
            max_login_attempts: 5,
        },
    });

    const loadSettings = useCallback(async () => {
        try {
            const response = await axios.get('/settings');

            setSettings(response.data);
        } catch (error) {
            console.error('Error loading settings:', error);
            toast.error(t('admin.errorSave'));
        }
    }, [t]);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    const handleChange = (section, field, value) => {
        setSettings(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value
            }
        }));
    };

    const handleSave = async () => {
        setIsLoading(true);
        try {
            await axios.post('/settings', settings);
            setIsLoading(false);
            toast.success(t('common.messages.settingsSaved'));
            
            // Apply language change if it was modified
            const currentLanguage = localStorage.getItem('language');
            if (settings.interface.default_language !== currentLanguage) {
                setLanguage(settings.interface.default_language);
            }
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error(t('common:errors.settingsSaveFailed'));
        }
    };

    const handleLanguageChange = (e) => {
        const newLanguage = e.target.value;
        handleChange('interface', 'default_language', newLanguage);
    };

    const tabs = [
        { id: 'restaurant', icon: FaStore, label: t('admin.restaurantSettings') },
        { id: 'printing', icon: FaPrint, label: t('admin.printerSettings') },
        { id: 'tax', icon: FaMoneyBillAlt, label: t('admin.taxSettings') },
        { id: 'interface', icon: FaLanguage, label: t('admin.interfaceSettings') },
        { id: 'receipts', icon: FaReceipt, label: t('admin.receiptSettings') },
        { id: 'delivery', icon: FaTruck, label: t('admin.deliverySettings') },
        { id: 'table_service', icon: FaMoneyBillAlt, label: t('admin.tableServiceSettings') },
        { id: 'users', icon: FaUserCog, label: t('admin.userSettingsTitle') }
    ];

    const renderTabContent = () => {
        switch (activeTab) {
            case 'restaurant':
                return <RestaurantTab settings={settings} handleChange={handleChange} />;
            case 'printing':
                return <PrintingTab settings={settings} handleChange={handleChange} />;
            case 'tax':
                return <TaxTab settings={settings} handleChange={handleChange} />;
            case 'interface':
                return <InterfaceTab settings={settings} handleChange={handleChange} handleLanguageChange={handleLanguageChange} />;
            case 'receipts':
                return <ReceiptsTab settings={settings} handleChange={handleChange} />;
            case 'delivery':
                return <DeliveryTab settings={settings} handleChange={handleChange} />;
            case 'table_service':
                return <TableServiceTab settings={settings} handleChange={handleChange} />;
            case 'users':
                return <UsersTab settings={settings} handleChange={handleChange} />;
            default:
                return null;
        }
    };

    return (
        <Layout>
            <div className="min-h-screen bg-gray-50 py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Header */}
                    <div className="mb-8">
                        <h1 className="text-3xl font-bold text-gray-900">{t('admin.settingsTitle')}</h1>
                        <p className="mt-2 text-sm text-gray-600">{t('admin.settingsDescription')}</p>
                    </div>

                    <div className="flex flex-col lg:flex-row gap-8">
                        {/* Sidebar */}
                        <div className="lg:w-64 flex-shrink-0">
                            <div className="bg-white rounded-lg shadow-sm p-1">
                                <nav className="space-y-1">
                                    {tabs.map(tab => (
                                        <button
                                            key={tab.id}
                                            onClick={() => setActiveTab(tab.id)}
                                            className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-md transition-all duration-200 ${
                                                activeTab === tab.id
                                                    ? 'bg-yellow-50 text-yellow-700'
                                                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                            }`}
                                        >
                                            <tab.icon className={`flex-shrink-0 h-5 w-5 ${
                                                activeTab === tab.id ? 'text-yellow-500' : 'text-gray-400'
                                            } ${isRTL ? 'ml-3' : 'mr-3'}`} />
                                            <span>{tab.label}</span>
                                        </button>
                                    ))}
                                </nav>
                            </div>
                        </div>

                        {/* Main Content */}
                        <div className="flex-1">
                            <div className="space-y-6">
                                {renderTabContent()}
                                
                                {/* Save Button */}
                                <div className={`flex ${isRTL ? 'justify-start' : 'justify-end'}`}>
                                    <button
                                        onClick={handleSave}
                                        disabled={isLoading}
                                        className={`
                                            inline-flex items-center px-6 py-3 
                                            bg-yellow-500 text-white rounded-lg 
                                            hover:bg-yellow-600 disabled:opacity-50
                                            transition-all duration-200
                                            focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2
                                            ${isLoading ? 'animate-pulse' : ''}
                                        `}
                                    >
                                        <FaSave className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                                        <span>{isLoading ? t('admin.saving') : t('admin.save')}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default Settings;